import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EventKindLegend, LoadError } from '@/components/common/bits';
import { SortMenu } from '@/components/common/SortMenu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Section, SectionHead, Surface } from '@/components/ui/section';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { AdminShell } from '@/features/admin/AdminShell';
import { useSort } from '@/hooks/use-sort';
import { isMilestone } from '@/lib/activity';
import { auditSortOptions, FAMILIES, type FamilyKey } from '@/lib/admin-sort';
import { api } from '@/lib/api';
import { type CollapsedEvent, collapseConsecutiveEvents } from '@/lib/collapse-events';
import { eventColor, isSubordinateKind, labelForKind } from '@/lib/event-kinds';
import { ago, clockTime, minutesSince } from '@/lib/format';
import { hubKeys } from '@/lib/query';
import { cn } from '@/lib/utils';

/**
 * The audit log: the whole record, verbatim, for the one role that should read
 * it.
 *
 * This page and `RecentActivity` are the two halves of a split that used to be
 * one firehose on everybody's home page. The home strip carries milestones in
 * short form; this carries every event exactly as the hub wrote it, machinery
 * and capacity arithmetic and email addresses included, because that is what an
 * audit record is and abbreviating it here would defeat the point of having one.
 *
 * It is admin-only, and that boundary is the server's: `/api/events` refuses a
 * member, and the route wraps this page in `AdminOnly` so the refusal is what
 * renders rather than an empty list. PRODUCT.md requires exactly that -- an
 * admin-only section must be server-enforced, never CSS-hidden.
 *
 * The three controls are the three questions an operator actually arrives with:
 * find a phrase, look at one family of events, or hide the dispatcher's chatter
 * to see what moved. Scheduler chatter is hidden by default (issue #90): the
 * first thing an admin used to see was the noisiest possible view of the
 * record, and the chip that hid it defaulted to off, which is backwards for a
 * control whose whole job is to say "something is filtered". The chip stays
 * visibly pressed while chatter is hidden, so turning it off is a decision
 * a person makes rather than one they never notice was made for them.
 *
 * `FAMILIES` and `auditSortOptions` live in `lib/admin-sort.ts` rather than
 * here, so the chips and the `family` sort key share one definition of what a
 * family is, and so a test can reach the key list without importing this
 * route module (see that file's own comment).
 *
 * The table only ever renders one page of rows (issue #90): `PAGE_SIZE` of
 * whatever `rows` currently holds, with a "Show more" control under it that
 * reveals another page at a time out of the up-to-300 the hub already sent.
 * That is not true pagination - the hub caps `/api/events` at 300 rows
 * (`packages/db/src/events.ts`) and there is no cursor yet to ask it for the
 * next 300 - it only bounds what this page draws out of what it already has.
 * A real cursor is a server change (a `beforeSeq`/`afterSeq` pair on
 * `listEvents` and the route that calls it) and is more than this issue's
 * scope; see the PR body.
 */

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-7 shrink-0 rounded-full px-3 text-xs font-medium transition-colors duration-100',
        active
          ? 'bg-foreground text-background'
          : 'text-fg-2 hover:bg-raised hover:text-foreground bg-transparent',
      )}
    >
      {children}
    </button>
  );
}

/** How many rows one screen of the audit log draws before "Show more" is the
    only way to see further into what is already fetched. */
const PAGE_SIZE = 100;

export function AdminAuditPage() {
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState<FamilyKey>('all');
  // Scheduler chatter hidden by default (issue #90): an admin's first look at
  // the record should be what moved, not the dispatcher's own bookkeeping.
  const [milestonesOnly, setMilestonesOnly] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const sorting = useSort<CollapsedEvent>(auditSortOptions(), (event) => event.id);

  /** A filter is a new question, so it gets its own first page rather than
      inheriting however far "Show more" had gotten on the last one. */
  const changeQuery = (next: string) => {
    setQuery(next);
    setVisible(PAGE_SIZE);
  };
  const changeFamily = (next: FamilyKey) => {
    setFamily(next);
    setVisible(PAGE_SIZE);
  };
  const toggleMilestonesOnly = () => {
    setMilestonesOnly((on) => !on);
    setVisible(PAGE_SIZE);
  };

  const events = useQuery({
    queryKey: [...hubKeys.events, 'audit'],
    queryFn: () => api.events(300),
    refetchInterval: 30_000,
  });

  // Consecutive, otherwise-identical rows collapse into one with a count
  // (issue #91): the record itself keeps every row a restart's hold-reason
  // burst wrote, but the log an operator scans should not repeat "T1 is
  // held: ..." thirty-seven times over. `total` below counts these rows, not
  // the raw ones underneath, since that is the record as this page reads it.
  const collapsed = useMemo(
    () => collapseConsecutiveEvents(events.data?.events ?? []),
    [events.data],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matcher = FAMILIES.find((entry) => entry.key === family) ?? FAMILIES[0];
    return collapsed
      .filter((event) => matcher.match(event.kind))
      .filter((event) => !milestonesOnly || isMilestone(event.kind))
      .filter(
        (event) =>
          !needle ||
          event.message.toLowerCase().includes(needle) ||
          (event.actor ?? '').toLowerCase().includes(needle) ||
          event.kind.toLowerCase().includes(needle),
      );
  }, [collapsed, query, family, milestonesOnly]);
  const rows = sorting.sort(filtered);
  // The bound this page actually draws (issue #90): `rows` can be up to the
  // 300 the hub sent, and rendering all of it at once is the same firehose
  // the "Hide scheduler chatter" chip exists to cut down on.
  const shown = rows.slice(0, visible);

  const total = collapsed.length;

  // What a column's header says to a screen reader. Only `time` and `family`
  // have a dedicated column here (the kind label, and the time on the right);
  // `actor` is a name inline in the message rather than a column of its own,
  // so it carries no header to mark.
  const ariaSortFor = (key: string): 'ascending' | 'descending' | 'none' =>
    sorting.state.key === key
      ? sorting.state.direction === 'asc'
        ? 'ascending'
        : 'descending'
      : 'none';

  return (
    <AdminShell tab="audit">
      <Section>
        <SectionHead
          title="The record"
          meta={
            rows.length === total
              ? `${total} event${total === 1 ? '' : 's'}`
              : `${rows.length} of ${total}`
          }
        />

        <div className="mb-2">
          <EventKindLegend />
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[14rem] flex-1">
            <Search
              className="text-fg-3 pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
              aria-hidden
            />
            <Input
              value={query}
              onChange={(event) => changeQuery(event.target.value)}
              placeholder="Search messages, actors, event kinds…"
              aria-label="Search the audit log"
              className="pl-8"
            />
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {FAMILIES.map((entry) => (
              <Chip
                key={entry.key}
                active={family === entry.key}
                onClick={() => changeFamily(entry.key)}
              >
                {entry.label}
              </Chip>
            ))}
            <span className="bg-line mx-1 h-5 w-px shrink-0" aria-hidden />
            {/* Pressed by default (issue #90): chatter is already hidden when
                this page opens, and the chip's own pressed state is what
                tells an admin something is filtered and they can turn it
                off. */}
            <Chip active={milestonesOnly} onClick={toggleMilestonesOnly}>
              Hide scheduler chatter
            </Chip>
            <span className="bg-line mx-1 h-5 w-px shrink-0" aria-hidden />
            <SortMenu options={sorting.options} state={sorting.state} onChange={sorting.choose} />
          </div>
        </div>

        <Surface>
          {shown.length ? (
            // A real `<table>` rather than the flex list this used to be
            // (issue #74): `aria-sort` only means something on a
            // `columnheader`, and biome's a11y rule agrees -- it refused the
            // ARIA-role version of this in favour of the elements that already
            // mean that. `Actor` has no header here: it is a name inline in
            // the message, not a column of its own, so its sort has nothing to
            // mark.
            // `table-fixed`, with an explicit width on every column except
            // Message: a message is a full sentence, and in an auto-layout
            // table it would size the whole table to its longest one, forcing
            // a horizontal scrollbar at a phone width instead of wrapping.
            // Fixed layout gives Message whatever is left over and wraps it
            // there -- checked at 375px and at 1280px.
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8" aria-hidden />
                  <TableHead aria-sort={ariaSortFor('family')} className="w-24 sm:w-36">
                    Kind
                  </TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead aria-sort={ariaSortFor('time')} className="w-20 text-right sm:w-28">
                    Time
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.map((event) => (
                  <TableRow
                    key={event.id}
                    className={cn(isSubordinateKind(event.kind) && 'opacity-70')}
                  >
                    <TableCell>
                      <span
                        className="block size-1.5 rounded-full"
                        style={{ background: eventColor(event.kind) }}
                        aria-hidden
                      />
                    </TableCell>
                    <TableCell className="text-fg-3 truncate text-xs" title={event.kind}>
                      {labelForKind(event.kind)}
                    </TableCell>
                    <TableCell className="text-fg-2 text-sm break-words">
                      {event.actor ? (
                        <span className="text-foreground font-medium">{event.actor} </span>
                      ) : null}
                      {event.message}
                      {event.count > 1 ? (
                        <span className="text-fg-3 ml-1.5 font-mono text-2xs">×{event.count}</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="tnum text-fg-3 text-right text-xs">
                      <time
                        dateTime={event.lastSeenAt}
                        title={
                          event.count > 1
                            ? `Last seen ${new Date(event.lastSeenAt).toLocaleString()}`
                            : new Date(event.createdAt).toLocaleString()
                        }
                      >
                        {clockTime(event.lastSeenAt)} · {ago(minutesSince(event.lastSeenAt))}
                      </time>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : events.isError && !events.data ? (
            // "The journal is empty" is the one thing this page must never say
            // when it does not know: an audit log nobody can read is a fact an
            // admin needs, and an empty one is a fact about the hub.
            <LoadError what="the record" error={events.error} retry={() => void events.refetch()} />
          ) : (
            <p className="text-fg-3 px-gutter py-12 text-center text-sm">
              {events.isPending
                ? 'Reading the journal…'
                : total
                  ? 'Nothing matches those filters.'
                  : 'The journal is empty. Every action anyone takes is recorded here.'}
            </p>
          )}
        </Surface>

        {rows.length > shown.length ? (
          // One page of the already-fetched rows at a time (issue #90): the
          // hub still caps `/api/events` at 300, so this reaches into what
          // came back with the request already made rather than asking for
          // more of the record.
          <div className="mt-3 flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setVisible((current) => current + PAGE_SIZE)}
            >
              Show more ({shown.length} of {rows.length})
            </Button>
          </div>
        ) : null}
      </Section>
    </AdminShell>
  );
}
